import React, { useEffect, useState } from "react";
import { GetStaticProps, GetStaticPaths } from "next";
import Head from "next/head";
import { useRouter } from "next/router";

import { brandsMetaDescription, partnerStatuses, sitemapPath } from "@/common";
import {
  instituteProps,
  investmentAppetiteType,
  FlexibleTableProps,
  OverViewDataType,
} from "@/common";
import {
  getCHProfileRecords,
  determineUser,
  getBuildStatus,
  generateSiteMap,
  generateSiteMapOfAllProfiles,
  reCreateFile,
  URL,
  shouldGenerateHtml,
  buildRelatedNewsSlug,
  parseRelatedNewsSlug,
  relatedNewsPageCount,
  splitAboutItems,
  mapWithConcurrency,
} from "@/utils";
import {
  useAppContext,
  ContactInformationComponent,
  InvestmentAppetiteComponent,
  FundsManagedComponent,
  FundCommitmentsComponent,
  Headlines,
  MentionedStories,
  OverViewComponent,
  RelatedNewsListing,
} from "@/components";
import {
  getHeadLines,
  getRelatedNewsCount,
  getRelatedNewsPageItems,
  getContacts,
  getInvestmentAppetite,
  getFundsManagedClosed,
  getFundsManagedInMarket,
  getFundCommitments,
  getProfilesByRegionAndPartnerStatus
} from "@/api";
import { SideBarNav, BreadCrumb, PageHeading } from "@/shared";
import { DataLayerScripts } from "@/components/DataLayerScripts";
import { useFirstName } from "@/hooks/useFirstName";
import { useZephrAccessDetails } from "@/hooks/useZephrAccessDetails";
import { assertProfileFetchesSucceeded } from "@/utils/buildGuard";

const { brand, shouldGenerateSitemap } = URL;

export default function Institutions(props: {
  Institution: instituteProps;
  firstAboutArticle: any;
  aboutItems: any[];
  mentionItems: any[];
  totalCount: number;
  investmentAppetite: investmentAppetiteType;
  hasApetiteRecord: boolean;
  officesCount: number;
  officesHQ: any;
  contacts: any;
  contactsCount: number;
  inMarketFundsCount: number;
  inMarketFunds: FlexibleTableProps;
  closedFundsCount: number;
  closedFunds: FlexibleTableProps;
  fundCommitments: any;
  fundCommitmentsCount: any;
  overviewData: OverViewDataType;
  dataLayerFeatureName: string;
  // Related News listing page (WEB-27455) — present only for related-news slugs.
  isRelatedNewsPage?: boolean;
  relatedNewsItems?: any[];
  relatedNewsBaseSlug?: string;
  relatedNewsPage?: number;
  relatedNewsPageCount?: number;
  canonicalUrl?: string;
  pageTitle?: string;
  pageDescription?: string;
}) {
  const { dispatch } = useAppContext();
  const router = useRouter();
  const [userType, setUserType] = useState("anonymous");
  const firstName = useFirstName();
  const accessDetails = useZephrAccessDetails();

  useEffect(() => {
    if (!accessDetails) {
      return;
    }

    const { isAuthenticated: loggedIn, activeProducts: products } = accessDetails;

    setUserType(determineUser({ loggedIn, products }));

    dispatch({
      type: "setUser",
      payload: { name: firstName, loggedIn },
    });
  }, [accessDetails, firstName, dispatch]);

  useEffect(() => {
    dispatch({
      type: "setInstituteAndPartnerStatus",
      payload: {
        partnerStatus: props.Institution?.PartnerStatus,
        institute: props.Institution?.Institution,
        institutionId: props.Institution?.ID,
      },
    });
  }, [dispatch, props]);

  if (router.isFallback) {
    return;
  }

  // Related News listing page (WEB-27455): one static, independently crawlable
  // page per 10 articles. Kept as an isolated branch so the profile-overview
  // path below is untouched.
  if (props.isRelatedNewsPage && props.Institution?.Institution) {
    const { Institution: relatedInstitution } = props.Institution;
    return (
      <React.Fragment>
        <Head>
          <meta name='description' content={props.pageDescription} />
          <title>{props.pageTitle}</title>
          {props.canonicalUrl && <link rel='canonical' href={props.canonicalUrl} />}
        </Head>
        <DataLayerScripts featureName={props.dataLayerFeatureName} institution={relatedInstitution} />
        <BreadCrumb title={relatedInstitution} />
        <section className='page-meta-section'>
          <SideBarNav />
          <PageHeading title={relatedInstitution} />
          <article className='page-main-content'>
            <RelatedNewsListing
              institutionName={relatedInstitution}
              baseSlug={props.relatedNewsBaseSlug ?? ''}
              backHref={`${props.relatedNewsBaseSlug}.html`}
              page={props.relatedNewsPage ?? 1}
              pageCount={props.relatedNewsPageCount ?? 1}
              items={props.relatedNewsItems ?? []}
            />
          </article>
        </section>
      </React.Fragment>
    );
  }

  const {
    hasApetiteRecord,
    investmentAppetite,
    contacts,
    officesCount,
    officesHQ,
    contactsCount,
    inMarketFunds,
    inMarketFundsCount,
    closedFundsCount,
    closedFunds,
    fundCommitments,
    fundCommitmentsCount,
    overviewData,
    dataLayerFeatureName,
  } = props;

  const { Institution, PartnerStatus, ID } = props.Institution;
  if (!Institution) {
    return;
  }
  const { description, title } = brandsMetaDescription(brand, Institution);
  return (
    <React.Fragment>
      <Head>
        <meta name='description' content={description} />
        <title>{title}</title>
      </Head>
      <DataLayerScripts featureName={dataLayerFeatureName} institution={Institution} />
      <BreadCrumb title={Institution} />
      <section className='page-meta-section'>
        <SideBarNav />
        <PageHeading title={Institution} />
        <article className='page-main-content'>
          <OverViewComponent
            institutionID={ID}
            partnerStatus={PartnerStatus}
            institutionName={Institution}
            overviewData={overviewData}
            userType={userType}
            name={firstName}
            firstAboutArticle={props.firstAboutArticle}
          />

          {props.aboutItems.length > 0 && (
            <Headlines
              institutionID={ID}
              userType={userType}
              partnerStatus={PartnerStatus}
              total={props.totalCount}
              institutionName={Institution}
              aboutItems={props.aboutItems}
              relatedNewsPageCount={props.relatedNewsPageCount}
              institutionSlug={Institution.formatString()}
            />
          )}


          {contacts.length > 0 && (
            <ContactInformationComponent
              institutionID={ID}
              partnerStatus={PartnerStatus}
              institutionName={Institution}
              officesCount={officesCount}
              officesHQ={officesHQ}
              contacts={contacts}
              contactsCount={contactsCount}
              userType={userType}
            />
          )}

          {hasApetiteRecord && (
            <InvestmentAppetiteComponent
              institutionId={ID}
              userType={userType}
              institutionName={Institution}
              records={investmentAppetite}
            />
          )}

          {fundCommitmentsCount > 0 && (
            <FundCommitmentsComponent
              userType={userType}
              institutionName={Institution}
              institutionID={ID}
              partnerStatus={PartnerStatus}
              fundCommitments={fundCommitments}
              fundCommitmentsCount={fundCommitmentsCount}
            />
          )}

          {(inMarketFunds.length > 0 || closedFunds.length > 0) && (
            <FundsManagedComponent
              userType={userType}
              institutionID={ID}
              partnerStatus={PartnerStatus}
              institutionName={Institution}
              inMarketFunds={inMarketFunds}
              inMarketFundsCount={inMarketFundsCount}
              closedFundsCount={closedFundsCount}
              closedFunds={closedFunds}
            />
          )}

          {props.mentionItems.length > 0 && (
            <MentionedStories
              institutionID={ID}
              userType={userType}
              partnerStatus={PartnerStatus}
              institutionName={Institution}
              mentionItems={props.mentionItems}
            />
          )}
        </article>
      </section>
    </React.Fragment>
  );
}

const RELATED_NEWS_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const formatNewsDate = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.getUTCDate()} ${RELATED_NEWS_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

// Turn an API related-news record into a display-ready row. The chip carries the
// article's editorial taxonomy (primaryCategory, e.g. "Commentary"); sector and
// strategy stand in when no category is set, and the chip is dropped entirely when
// the record carries none — the field is sparsely populated.
const toRelatedNewsItem = (record: any, institutionId: string) => {
  const authors = Array.isArray(record?.authorNames)
    ? record.authorNames.filter(Boolean).join(", ")
    : typeof record?.authorNames === "string"
      ? record.authorNames
      : "";
  return {
    title: record?.title ?? record?.headline ?? "",
    summary: record?.summary ?? "",
    url: `${URL.basePEIURL}/${record?.postName ?? ""}`,
    thumbnail: record?.thumbnail ?? "",
    category: record?.primaryCategory || record?.primarySector || record?.primaryStrategy || null,
    author: authors || null,
    date: formatNewsDate(record?.createdDate),
  };
};

// Build-time props for a single Related News listing page (WEB-27455). The page is
// carved from the firm's filtered set rather than from the API page of the same
// index, because RA API's page windows come back short (see getRelatedNewsPageItems).
const getRelatedNewsStaticProps = async (baseSlug: string, page: number) => {
  const CHProfiles: instituteProps[] = await getCHProfileRecords(brand, false);
  const instituteRec = CHProfiles.find(
    (x: instituteProps) => x.Institution.formatString() === baseSlug
  );
  if (!instituteRec) {
    return { notFound: true as const };
  }

  const { records, total } = await getRelatedNewsPageItems(instituteRec.ID, page);
  const pageCount = relatedNewsPageCount(total);
  if (records.length === 0 || total === 0 || page > pageCount) {
    return { notFound: true as const };
  }

  const items = records.map((r: any) => toRelatedNewsItem(r, instituteRec.ID));

  const { title: profileTitle, description } = brandsMetaDescription(brand, instituteRec.Institution);
  const pageTitle = profileTitle.replace("Institution Profile", "Related News");

  return {
    props: {
      isRelatedNewsPage: true,
      Institution: instituteRec,
      relatedNewsItems: items,
      relatedNewsBaseSlug: baseSlug,
      relatedNewsPage: page,
      relatedNewsPageCount: pageCount,
      canonicalUrl: `${URL.canonicalPEIURL}/institution-profiles/${buildRelatedNewsSlug(baseSlug, page)}.html`,
      pageTitle: page > 1 ? `${pageTitle} - Page ${page}` : pageTitle,
      pageDescription: description,
      dataLayerFeatureName:
        process.env["DATALAYER_FEATURE_NAME"] ||
        new Error("DATALAYER_FEATURE_NAME env var not defined"),
      firstAboutArticle: null,
      aboutItems: [],
      mentionItems: [],
    },
  };
};

// This function gets called at build time
export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = (params?.Institution as string) || "";
  const instituteRec = await verifyIfProfileExist(params);
  if (!instituteRec) {
    // Overview slug takes precedence; only non-profile slugs are treated as a
    // Related News listing (WEB-27455).
    const relatedNews = parseRelatedNewsSlug(slug);
    if (relatedNews) {
      const relatedNewsProps = await getRelatedNewsStaticProps(relatedNews.baseSlug, relatedNews.page);
      if (!(relatedNewsProps as { notFound?: boolean }).notFound) {
        return relatedNewsProps;
      }
      // getStaticPaths sized the pager off pagination.total, which the API reports
      // before it filters the records it returns — so a page counted here can still
      // come back empty. Emit nothing rather than publish a blank, indexable page.
      return { notFound: true };
    }
    return {
      props: {
        Institution: [],
        firstAboutArticle: null,
        aboutItems: [],
        mentionItems: [],
        totalCount: 0,
      },
    };
  }
  const { ID, Institution, PartnerStatus } = instituteRec;

  const [
    { aboutItems, mentionItems, totalCount },
    { officesCount, officesHQ, contacts, contactsCount },
    { investmentAppetite, hasApetiteRecord, overviewData },
    { inMarketFundsCount, inMarketFunds },
    { closedFundsCount, closedFunds },
    { fundCommitmentsCount, fundCommitments },
    // No `await` inside this array: awaiting each call while building it makes
    // Promise.all receive six already-resolved values, i.e. six round trips in
    // series for every profile page — the dominant cost of the whole build.
  ] = await Promise.all([
    getHeadLines(ID),
    getContacts(ID),
    getInvestmentAppetite(ID, PartnerStatus),
    getFundsManagedInMarket(ID, PartnerStatus),
    getFundsManagedClosed(ID, PartnerStatus),
    getFundCommitments(ID, PartnerStatus),
  ]);

  // WEB-27455: page count for the "Stories about" pager. getHeadLines already
  // read pagination.total off the same related-news endpoint, so this costs no
  // extra request — and it is the same total getStaticPaths counted paths from.
  const relatedNewsPages = relatedNewsPageCount(totalCount);

  const buildStatus = getBuildStatus(
    totalCount,
    contacts,
    fundCommitmentsCount,
    hasApetiteRecord,
    inMarketFunds,
    closedFunds,
    PartnerStatus
  );

  if (shouldGenerateSitemap && !buildStatus) {
    await generateSiteMap(Institution);
  }

  if (!shouldGenerateHtml(buildStatus)) {
    return {
      notFound: true,
    };
  }

  // WEB-32715 (AC4): the Featured slot takes the newest About article whenever one
  // exists, and never also appears in the grid. Split lives in the util so it is
  // unit-testable — getStaticProps itself has no test harness.
  const { firstAboutArticle, aboutItemsProp } = splitAboutItems(aboutItems);

  return {
    props: {
      Institution: instituteRec,
      firstAboutArticle,
      aboutItems: aboutItemsProp,
      mentionItems,
      totalCount: totalCount,
      investmentAppetite: investmentAppetite,
      hasApetiteRecord: hasApetiteRecord,
      officesCount,
      officesHQ,
      contacts,
      contactsCount,
      inMarketFundsCount,
      inMarketFunds,
      closedFundsCount,
      closedFunds,
      fundCommitments,
      fundCommitmentsCount,
      overviewData,
      relatedNewsPageCount: relatedNewsPages,
      dataLayerFeatureName: process.env['DATALAYER_FEATURE_NAME'] || new Error('DATALAYER_FEATURE_NAME env var not defined'),
    },
  };
};

// This function gets called at build time
export const getStaticPaths: GetStaticPaths = async () => {
  const targetInstitution = process.env.BUILD_INSTITUTION_NAME;
  if (targetInstitution) {
    const allProfiles = await getCHProfileRecords(brand, false);
    const isId = targetInstitution.startsWith("institution:");
    const match = allProfiles.find((p: instituteProps) =>
      isId
        ? p.ID === targetInstitution
        : p.Institution.formatString() === targetInstitution.formatString()
    );
    if (!match) {
      const hint = isId
        ? `No profile with ID "${targetInstitution}" found for brand "${brand}".`
        : `No profile matching name "${targetInstitution}" found for brand "${brand}". Check the name matches exactly (case-insensitive).`;
      throw new Error(hint);
    }
    const matchSlug = match.Institution.formatString();
    const paths: { params: { Institution: string } }[] = [
      { params: { Institution: matchSlug } },
    ];
    try {
      const total = await getRelatedNewsCount(match.ID);
      for (let p = 1; total > 0 && p <= relatedNewsPageCount(total); p++) {
        paths.push({ params: { Institution: buildRelatedNewsSlug(matchSlug, p) } });
      }
    } catch (err) {
      console.error(`[getStaticPaths] related-news paths failed for ${match.ID}`, err);
    }
    return { paths, fallback: false };
  }

  try {
    reCreateFile(
      sitemapPath,
      "<urlset xmlns='http://www.sitemaps.org/schemas/sitemap/0.9'></urlset>"
    );

    // comment these lines of code to fall back to the orignal profile.json
    // start
    if (!JSON.parse(process.env.BUILD_LOCAL_PROFILES || "false")) {
      const isLPProfiles = await getProfilesByRegionAndPartnerStatus(partnerStatuses.LP);
      const isGPProfiles = await getProfilesByRegionAndPartnerStatus(partnerStatuses.GP);
      assertProfileFetchesSucceeded(isLPProfiles, isGPProfiles);
    }
    // end

    const CHProfiles: instituteProps[] = await getCHProfileRecords(brand);
    const invalidProfiles = CHProfiles.filter((profile: instituteProps) => !profile.Institution);
    if (invalidProfiles.length > 0) {
      invalidProfiles.forEach((profile: instituteProps) => console.error(`[getStaticPaths] Profile with empty slug skipped — ID: ${profile.ID}, Institution: ${profile.Institution}`));
      throw new Error(`[getStaticPaths] Found ${invalidProfiles.length} profile(s) with empty slug after formatting. Fix the source data before building.`);
    }
    const validProfiles = CHProfiles.filter((profile: instituteProps) => !!profile.Institution);
    const paths = validProfiles.map((profile: instituteProps) => ({
      params: { Institution: profile.Institution },
    }));
    // WEB-27455: one flat, depth-1, independently-crawlable page per page of
    // related news, per profile (page 1 = <slug>-related-news, then -page-N).
    //
    // This runs inside Next's staticPageGenerationTimeout window, once per profile
    // — ~16k of them on PEI. The count walks the firm's history in batches of
    // RELATED_NEWS_WALK_SIZE, and the worst firm on stage has 107 articles, so in
    // practice every profile is still ONE request. Each walk is sequential, so peak
    // in-flight requests stays at the concurrency limit below.
    const countConcurrency = Number(process.env.RELATED_NEWS_COUNT_CONCURRENCY) || 16;
    let countFailures = 0;
    console.info(
      `[getStaticPaths] resolving related news counts for ${validProfiles.length} profiles (concurrency ${countConcurrency})...`
    );
    const relatedNewsTotals = await mapWithConcurrency(
      validProfiles,
      countConcurrency,
      async (profile: instituteProps) => {
        try {
          return await getRelatedNewsCount(profile.ID);
        } catch (err) {
          countFailures++;
          console.error(`[getStaticPaths] related-news count failed for ${profile.ID}`, err);
          return 0;
        }
      }
    );
    const relatedNewsPaths: { params: { Institution: string } }[] = [];
    validProfiles.forEach((profile: instituteProps, i: number) => {
      const total = relatedNewsTotals[i];
      for (let p = 1; total > 0 && p <= relatedNewsPageCount(total); p++) {
        relatedNewsPaths.push({
          params: { Institution: buildRelatedNewsSlug(profile.Institution, p) },
        });
      }
    });
    // Loud, because a swallowed count failure means a firm's Related News pages
    // are silently absent from the build.
    if (countFailures > 0) {
      console.warn(
        `[getStaticPaths] WARNING: related-news counts failed for ${countFailures} of ${validProfiles.length} profiles — those firms get no Related News pages this build.`
      );
    }
    console.info(
      `[getStaticPaths] emitting ${relatedNewsPaths.length} related-news pages for ${paths.length} profiles.`
    );
    const allPaths = [...paths, ...relatedNewsPaths];
    // The sitemap lists profile pages only; Related News pages are reached by
    // crawling the profile's links.
    if (shouldGenerateSitemap) {
      await generateSiteMapOfAllProfiles(paths);
    }
    return { paths: allPaths, fallback: true };
  } catch (err: any) {
    throw new Error(
      `Failed to fetch Content Hub profiles. Build stopped. Cause: ${err?.message ?? err}`
    );
  }
};

const verifyIfProfileExist = async (params: any) => {
  const CHProfiles: instituteProps[] = await getCHProfileRecords(brand, false);
  let institutionName = params?.Institution as string;
  institutionName = institutionName.formatString();
  // Verify profile names with our .json file
  const instituteRec = CHProfiles.find(
    (x: instituteProps) => x.Institution.formatString() === institutionName
  );
  return instituteRec;
};
